import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Loader2, IndianRupee, Receipt, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentHistoryPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API}/api/subscription/payments`, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (r.ok) {
          const data = await r.json();
          setRows(data.rows || []);
        }
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const downloadReceiptPdf = async (row) => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/subscription/payments/${row.razorpay_order_id}/receipt.pdf`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error('PDF unavailable');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cognitiaindia_receipt_${row.razorpay_payment_id || row.razorpay_order_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // Fallback to HTML
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>CognitiaIndia Receipt</title>
        <style>body{font-family:system-ui,sans-serif;max-width:560px;margin:40px auto;padding:24px;color:#0f172a}
          h1{margin:0 0 16px 0} .row{padding:8px 0;border-bottom:1px solid #e2e8f0;display:flex;justify-content:space-between}
          .amt{font-size:22px;font-weight:800;color:#059669}</style></head><body>
        <h1>CognitiaIndia — Payment Receipt</h1>
        <div class="row"><span>Plan</span><span>${row.plan}</span></div>
        <div class="row"><span>Amount (GST included)</span><span class="amt">₹${row.amount_inr}</span></div>
        <div class="row"><span>Payment reference</span><span>${row.razorpay_payment_id || '—'}</span></div>
        <div class="row"><span>Order ID</span><span>${row.razorpay_order_id}</span></div>
        <div class="row"><span>Verified on</span><span>${row.verified_at ? new Date(row.verified_at).toLocaleString() : '—'}</span></div>
      </body></html>`;
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cognitiaindia_receipt_${row.razorpay_payment_id || row.razorpay_order_id}.html`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="payment-history-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="ph-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Payment History</h1>
            <p className="text-sm text-muted-foreground">Every subscription payment you have made, with downloadable receipts.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
        ) : rows.length === 0 ? (
          <Card data-testid="ph-empty">
            <CardContent className="p-8 text-center">
              <Receipt className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No payments yet. Once you subscribe, your receipts will appear here.</p>
              <Button className="mt-4" onClick={() => navigate('/subscription')} data-testid="ph-go-subscribe">See plans</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {rows.map((r, i) => (
              <Card key={r.razorpay_order_id} data-testid={`ph-row-${i}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                    <IndianRupee className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-sm capitalize">{r.plan} plan</p>
                      <Badge variant="outline" className="text-[10px]">{r.status}</Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {r.verified_at ? new Date(r.verified_at).toLocaleString() : ''} · Ref {r.razorpay_payment_id || r.razorpay_order_id}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-black">₹{r.amount_inr}</p>
                    <p className="text-[10px] text-muted-foreground">GST included</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => downloadReceiptPdf(r)} data-testid={`ph-download-pdf-${i}`}>
                    <Download className="w-3.5 h-3.5 mr-1" />PDF
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
